import { batch } from "react-redux";

import {
   createReservationsForUser,
   deleteReservation,
   patchReservation,
} from "../../api/reservationsService";
import {
   addReservationLocal,
   fetchReservationsDelta,
   patchReservationLocal,
   removeReservationLocal,
} from "../../store/reservationsSlice";
import { scheduleCalendarRefresh } from "../../components/Utils/calendarBus";
import { formatCalendarCreateError } from "../../components/Utils/calendarCreateErrors";
import {
   extractCreatedReservations,
   getReservationColumnInstructorId,
   getReservationStudentId,
   hasActiveReservationInSlot,
   parsePositiveInt,
   reservationIdFromAny,
   reservationStartFromAny,
} from "../ultra/reservationRowUtils";
import {
   applyOptimisticRemove,
   applyOptimisticReplaceId,
   applyOptimisticUpsert,
   compactPatchBusAfterIdPromotion,
   completePendingCommand,
} from "../master/mutations/calendarMutationSync";
import { applyReservationGridPatch } from "./applyReservationGridPatch";
import {
   buildOptimisticSlotVisualItem,
   buildReservationStatusMeta,
   normalizeSlotVisualColor,
} from "./buildOptimisticSlotVisualItem";
import { CALENDAR_LOCAL_ECHO_TTL_MS } from "../sync/tombstones";
import { blockSlotSingle } from "./blockSlotAction";
import { resolveCreatedReservationId } from "./resolveCreatedReservationId";
import { ULTRA_LATERAL_INSTRUCTOR_ID } from "./constants";

export async function pasteReservationSlot(ctx, slotArgs) {
   const {
      copyBufferRef,
      dispatch,
      slotPatchBusRef,
      reservationSlotsMap,
      blackoutSlotsSet,
      studentsById,
      usersById,
      normalizedTimeLabels,
      timeLabelMinutes,
      clearSocketDeletedReservationIds,
      pushUltraAlert,
      resolveCopyPayloadUserId,
      resolveSlotStartPayload,
      resolveTargetInstructorGearboxLabel,
      helpers,
   } = ctx;

   const {
      normalizeHourMinuteLabel,
      formatDateKeyInChisinau,
      formatTimeLabelInChisinau,
      findNearestTimeSlotLabel,
      toFloatingDate,
      getInstructorSectorSlugById,
      sectorSlugToLabel,
      hasSectorMismatchWithInstructor,
      normalizeColorForCreate,
      hasMeaningfulCalendarEventColor,
      getPersonFullName,
      getPersonPhone,
   } = helpers;

   const abort = (message) => {
      const reason = String(message || "").trim() || "Lipirea a fost respinsă.";
      pushUltraAlert?.("error", reason);
      return { ok: false, reason, alreadyAlerted: true };
   };

   const slotHasLiveReservation = (slotKey, items) => {
      const list = Array.isArray(items) ? items : [];
      const store = ctx.calendarStore || null;
      for (const item of list) {
         if (!item || item.isCanceled === true) continue;
         const id = String(item?.id || "").trim();
         // After cut/delete the map can briefly keep a ghost; tombstone wins.
         if (id && store?.isTombstoned?.(id) === true) continue;
         if (id && store && !store.getEventById?.(id) && store.isTombstoned?.(id)) {
            continue;
         }
         return true;
      }
      if (!store || typeof store.getSlotEventIds !== "function") {
         return hasActiveReservationInSlot(list);
      }
      for (const rawId of store.getSlotEventIds(slotKey) || []) {
         const id = String(rawId || "").trim();
         if (!id || store.isTombstoned?.(id) === true) continue;
         const event = store.getEventById?.(id);
         if (event && event.isCanceled !== true) return true;
      }
      return false;
   };

   const { dayIndex, instructorId, slotLabel, cellType } = slotArgs || {};
   const copyPayload = copyBufferRef?.current;
   if (!copyPayload) {
      return abort("Nu ai nicio lecție copiată. Copiază una întâi.");
   }

   const resolvedDayIndex = Number(dayIndex);
   const resolvedInstructorId = String(instructorId || "").trim();
   const resolvedCellType = String(cellType || "").trim().toLowerCase();
   const resolvedSlotLabel = normalizeHourMinuteLabel(slotLabel);
   if (
      !Number.isFinite(resolvedDayIndex) ||
      resolvedDayIndex < 0 ||
      !resolvedInstructorId ||
      !resolvedSlotLabel
   ) {
      return abort("Selectează o celulă validă înainte de a lipi lecția.");
   }

   const isLateralTarget =
      resolvedCellType === "lateral-hour" ||
      resolvedInstructorId === ULTRA_LATERAL_INSTRUCTOR_ID;

   const targetInstructorRaw = isLateralTarget
      ? ULTRA_LATERAL_INSTRUCTOR_ID
      : resolvedInstructorId;
   const parsedInstructorId = parsePositiveInt(targetInstructorRaw);
   if (!parsedInstructorId) {
      return abort("Coloana selectată nu are un instructor valid.");
   }
   const parsedUserId = resolveCopyPayloadUserId(copyPayload);
   if (!parsedUserId) {
      return abort(
         "Lecția copiată nu are un elev valid asociat, deci nu poate fi lipită.",
      );
   }

   const targetInstructorKey = String(parsedInstructorId).trim();
   const targetInstructorSectorSlug = getInstructorSectorSlugById(
      targetInstructorKey,
      ctx.instructorObjectById,
      ctx.instructorSectorById,
   );
   const syncedSectorLabel = sectorSlugToLabel(
      targetInstructorSectorSlug,
      String(copyPayload?.sector || "").trim() || "Botanica",
   );
   const copyGearboxLabel =
      String(copyPayload?.gearbox || "Manual").trim() || "Manual";
   const syncedGearboxLabel = isLateralTarget
      ? copyGearboxLabel
      : resolveTargetInstructorGearboxLabel(targetInstructorKey) || copyGearboxLabel;

   const slotKey = `${resolvedDayIndex}|${String(parsedInstructorId)}|${resolvedSlotLabel}`;
   const isBlackoutSlot =
      blackoutSlotsSet?.has?.(slotKey) || resolvedCellType === "blocked";
   if (isBlackoutSlot) {
      const unblockResult = await blockSlotSingle(ctx, slotArgs, "unlock");
      if (!unblockResult?.ok) {
         return abort(
            "Slotul este blocat și nu a putut fi deblocat pentru lipire.",
         );
      }
   }
   const existingItems = reservationSlotsMap?.get?.(slotKey);
   if (slotHasLiveReservation(slotKey, existingItems)) {
      return abort("Slotul este deja ocupat. Alege o casetă liberă.");
   }

   const payloadSlot = resolveSlotStartPayload(resolvedDayIndex, resolvedSlotLabel);
   if (!payloadSlot?.startTimeToSend) {
      return abort("Nu am putut calcula ora pentru caseta selectată.");
   }

   const reservationToCreate = {
      startTime: payloadSlot.startTimeToSend,
      sector: syncedSectorLabel,
      gearbox: syncedGearboxLabel,
      privateMessage: String(copyPayload?.privateMessage || ""),
      isFavorite: copyPayload?.isFavorite === true,
      isImportant: copyPayload?.isImportant === true,
      instructorId: parsedInstructorId,
   };
   const colorForCreate = normalizeColorForCreate(copyPayload?.color);
   const shouldPreserveColor =
      Boolean(colorForCreate) &&
      (hasMeaningfulCalendarEventColor(colorForCreate) ||
         /^(?:--)?(?:event-)?default$/i.test(colorForCreate));
   if (shouldPreserveColor) {
      reservationToCreate.color = colorForCreate;
   }

   const optimisticId = `tmp-ultra-paste-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
   const optimisticStartTime = `${payloadSlot.dayKey}T${payloadSlot.slotLabel}:00`;
   const pastePerson =
      studentsById?.get?.(String(parsedUserId)) ||
      usersById?.get?.(String(parsedUserId)) ||
      null;
   const pasteTitle =
      getPersonFullName(pastePerson) ||
      String(copyPayload?.title || "").trim() ||
      "Rezervare";
   const pastePhone = getPersonPhone(pastePerson) || "";
   const pasteInstructorSectorSlug = getInstructorSectorSlugById(
      targetInstructorKey,
      ctx.instructorObjectById,
      ctx.instructorSectorById,
   );

   const pasteVisualItem = buildOptimisticSlotVisualItem({
      id: optimisticId,
      userId: String(parsedUserId),
      instructorId: String(parsedInstructorId),
      title: pasteTitle,
      phone: pastePhone,
      note: String(copyPayload?.privateMessage || ""),
      privateMessage: String(copyPayload?.privateMessage || ""),
      color: normalizeSlotVisualColor(colorForCreate || copyPayload?.color),
      isFavorite: reservationToCreate.isFavorite === true,
      isImportant: reservationToCreate.isImportant === true,
      sector: syncedSectorLabel,
      gearbox: syncedGearboxLabel,
      hasSectorMismatch: hasSectorMismatchWithInstructor(
         syncedSectorLabel,
         pasteInstructorSectorSlug,
      ),
      timeText: resolvedSlotLabel,
      meta: buildReservationStatusMeta({
         isFavorite: reservationToCreate.isFavorite === true,
         isImportant: reservationToCreate.isImportant === true,
         gearbox: syncedGearboxLabel,
      }),
   });
   const optimisticReservation = {
      id: optimisticId,
      userId: parsedUserId,
      instructorId: parsedInstructorId,
      startTime: optimisticStartTime,
      sector: reservationToCreate.sector,
      gearbox: reservationToCreate.gearbox,
      privateMessage: reservationToCreate.privateMessage,
      color: reservationToCreate.color || "",
      isFavorite: reservationToCreate.isFavorite === true,
      isImportant: reservationToCreate.isImportant === true,
      _optimistic: true,
      _optimisticPending: true,
   };

   const calendarStore = ctx.calendarStore || null;
   applyOptimisticUpsert(calendarStore, {
      event: optimisticReservation,
      slotKey,
      commandId: String(ctx.activeCommandId || ""),
      patchBus: slotPatchBusRef?.current,
      visualItem: pasteVisualItem,
   });

   dispatch(
      addReservationLocal({
         id: optimisticId,
         userId: parsedUserId,
         instructorId: parsedInstructorId,
         startTime: optimisticStartTime,
         sector: reservationToCreate.sector,
         gearbox: reservationToCreate.gearbox,
         privateMessage: reservationToCreate.privateMessage,
         color: reservationToCreate.color || "",
         isFavorite: reservationToCreate.isFavorite === true,
         isImportant: reservationToCreate.isImportant === true,
         _optimistic: true,
         _optimisticPending: true,
      }),
   );

   try {
      const createResult = await createReservationsForUser({
         userId: parsedUserId,
         instructorId: parsedInstructorId,
         reservations: [reservationToCreate],
      });
      pushUltraAlert?.("success", "Programarea a fost creată cu succes.");

      if (isBlackoutSlot) {
         try {
            scheduleCalendarRefresh({
               source: "calendar",
               type: "blackouts-changed",
               instructorId: String(parsedInstructorId),
               forceReload: false,
            });
         } catch {
            // ignore refresh bus errors
         }
         ctx.setBlackoutRefreshTick?.((prev) => prev + 1);
      }

      // M2 deterministic path: prefer id returned by create API.
      const createdResolution = resolveCreatedReservationId(createResult, {
         userId: parsedUserId,
         instructorId: parsedInstructorId,
      });
      let confirmedId = createdResolution.confirmedId || "";
      let bestCandidate = createdResolution.candidate;
      let resolveSource = createdResolution.source;

      const targetSignature = `${payloadSlot.dayKey}|${resolvedSlotLabel}`;
      const evaluateCandidate = (candidate) => {
         if (!candidate || typeof candidate !== "object") {
            return {
               score: Number.NEGATIVE_INFINITY,
               studentMatch: false,
               instructorMatch: false,
               slotMatch: false,
            };
         }
         let score = 0;

         const candidateId = reservationIdFromAny(candidate);
         if (candidateId) score += 2;

         const candidateUserId = String(getReservationStudentId(candidate) ?? "").trim();
         const studentMatch =
            Boolean(candidateUserId) && candidateUserId === String(parsedUserId);
         if (studentMatch) score += 4;

         const candidateInstructorId = String(
            getReservationColumnInstructorId(candidate) ?? "",
         ).trim();
         const instructorMatch =
            Boolean(candidateInstructorId) &&
            candidateInstructorId === String(parsedInstructorId);
         if (instructorMatch) score += 4;

         let slotMatch = false;
         const candidateStart = toFloatingDate(reservationStartFromAny(candidate));
         if (candidateStart) {
            const dayKey = formatDateKeyInChisinau(candidateStart);
            const hmRaw = normalizeHourMinuteLabel(
               formatTimeLabelInChisinau(candidateStart),
            );
            const slotLabelMatch = findNearestTimeSlotLabel(
               hmRaw,
               normalizedTimeLabels,
               timeLabelMinutes,
            );
            if (
               dayKey &&
               slotLabelMatch &&
               `${dayKey}|${slotLabelMatch}` === targetSignature
            ) {
               slotMatch = true;
               score += 8;
            }
         }

         return { score, studentMatch, instructorMatch, slotMatch };
      };

      // Fallback only when create response had no usable id.
      if (!confirmedId) {
         const createdCandidates = extractCreatedReservations(createResult);
         const deltaAction = await dispatch(fetchReservationsDelta());
         const deltaItems = Array.isArray(deltaAction?.payload?.items)
            ? deltaAction.payload.items
            : [];
         const candidatesForMatch = [...createdCandidates, ...deltaItems];

         let bestEval = null;
         bestCandidate = null;
         for (const candidate of candidatesForMatch) {
            const evaluation = evaluateCandidate(candidate);
            if (!bestEval || evaluation.score > bestEval.score) {
               bestEval = evaluation;
               bestCandidate = candidate;
            }
         }

         const isStrongCandidateMatch = Boolean(
            bestEval &&
               bestEval.studentMatch &&
               bestEval.instructorMatch &&
               bestEval.slotMatch,
         );
         confirmedId = isStrongCandidateMatch
            ? reservationIdFromAny(bestCandidate) || ""
            : "";
         resolveSource = confirmedId ? "delta-strong" : "unresolved";
      }

      void resolveSource;

      // Copied color must win over the create-response echo. POST /for-user can
      // re-derive profile/default colors; preferring that echo made paste lose
      // the source lesson's color after confirm / month refetch.
      const copiedColor = String(reservationToCreate.color || "").trim();
      const serverColorRaw = normalizeColorForCreate(
         bestCandidate?.color ?? bestCandidate?.reservation?.color ?? "",
      );
      const patchColor = hasMeaningfulCalendarEventColor(copiedColor)
         ? copiedColor
         : hasMeaningfulCalendarEventColor(serverColorRaw)
           ? serverColorRaw
           : copiedColor || serverColorRaw || "";

      if (confirmedId) {
         const deletedWhilePasting =
            calendarStore?.isTombstoned?.(String(confirmedId)) === true ||
            (calendarStore?.isTombstoned?.(optimisticId) === true &&
               !calendarStore?.getEventById?.(optimisticId) &&
               !calendarStore?.getEventById?.(String(confirmedId)));

         if (deletedWhilePasting) {
            dispatch(removeReservationLocal(optimisticId));
            dispatch(removeReservationLocal(confirmedId));
            applyOptimisticRemove(calendarStore, {
               eventId: optimisticId,
               patchBus: slotPatchBusRef?.current,
               tombstone: true,
            });
            applyOptimisticRemove(calendarStore, {
               eventId: String(confirmedId),
               patchBus: slotPatchBusRef?.current,
               tombstone: true,
            });
            void deleteReservation(confirmedId).catch(() => {});
            completePendingCommand(calendarStore, optimisticId);
            completePendingCommand(calendarStore, confirmedId);
            return { ok: true, cancelledByDelete: true };
         }

         if (!calendarStore?.isTombstoned?.(String(confirmedId))) {
            clearSocketDeletedReservationIds?.([confirmedId]);
         }
         const confirmedReservation = {
            id: confirmedId,
            userId: parsedUserId,
            instructorId: parsedInstructorId,
            startTime: optimisticStartTime,
            sector: String(
               bestCandidate?.sector ??
                  bestCandidate?.reservation?.sector ??
                  reservationToCreate.sector ??
                  "Botanica",
            ).trim(),
            gearbox: String(
               bestCandidate?.gearbox ??
                  bestCandidate?.reservation?.gearbox ??
                  reservationToCreate.gearbox ??
                  "Manual",
            ).trim(),
            privateMessage: String(
               bestCandidate?.privateMessage ??
                  bestCandidate?.reservation?.privateMessage ??
                  reservationToCreate.privateMessage ??
                  "",
            ),
            color: patchColor,
            isFavorite:
               bestCandidate?.isFavorite ??
               bestCandidate?.is_favorite ??
               bestCandidate?.reservation?.isFavorite ??
               bestCandidate?.reservation?.is_favorite ??
               reservationToCreate.isFavorite === true,
            isImportant:
               bestCandidate?.isImportant ??
               bestCandidate?.is_important ??
               bestCandidate?.reservation?.isImportant ??
               bestCandidate?.reservation?.is_important ??
               reservationToCreate.isImportant === true,
            _optimistic: false,
            _optimisticPending: false,
            _localEchoPending: confirmedId !== optimisticId,
            _localEchoUntil:
               confirmedId !== optimisticId ? Date.now() + CALENDAR_LOCAL_ECHO_TTL_MS : null,
         };

         let replaced = true;
         batch(() => {
            if (confirmedId !== optimisticId) {
               dispatch(removeReservationLocal(confirmedId));
            }
            replaced = applyOptimisticReplaceId(calendarStore, {
               fromId: optimisticId,
               toId: String(confirmedId),
               nextEvent: confirmedReservation,
               slotKey,
               patchBus: slotPatchBusRef?.current,
            });
            if (!replaced) {
               dispatch(removeReservationLocal(optimisticId));
               dispatch(removeReservationLocal(confirmedId));
               return;
            }
            dispatch(
               patchReservationLocal({
                  id: optimisticId,
                  changes: confirmedReservation,
               }),
            );
         });
         if (!replaced) {
            void deleteReservation(confirmedId).catch(() => {});
            completePendingCommand(calendarStore, optimisticId);
            completePendingCommand(calendarStore, confirmedId);
            return { ok: true, cancelledByDelete: true };
         }
         compactPatchBusAfterIdPromotion(slotPatchBusRef?.current, {
            fromId: optimisticId,
            toId: String(confirmedId),
            slotKey,
         });
         applyReservationGridPatch({
            reservation: confirmedReservation,
            slotKey,
            visualItem: {
               ...pasteVisualItem,
               id: String(confirmedId),
               color: normalizeSlotVisualColor(patchColor),
            },
            calendarStore,
            patchBus: slotPatchBusRef?.current,
         });
         if (
            shouldPreserveColor &&
            confirmedId &&
            !String(confirmedId).startsWith("tmp-") &&
            serverColorRaw !== colorForCreate
         ) {
            void patchReservation(confirmedId, {
               color: colorForCreate,
            }).catch(() => {});
         }
         completePendingCommand(calendarStore, optimisticId);
         completePendingCommand(calendarStore, confirmedId);
      } else {
         dispatch(
            patchReservationLocal({
               id: optimisticId,
               changes: {
                  _optimistic: false,
                  _optimisticPending: false,
                  _localEchoPending: true,
                  _localEchoUntil: Date.now() + CALENDAR_LOCAL_ECHO_TTL_MS,
               },
            }),
         );
         completePendingCommand(calendarStore, optimisticId);
      }

      return {
         ok: true,
         optimisticId,
         confirmedId: confirmedId || optimisticId,
      };
   } catch (error) {
      applyOptimisticRemove(calendarStore, {
         eventId: optimisticId,
         patchBus: slotPatchBusRef?.current,
         tombstone: false,
      });
      dispatch(removeReservationLocal(optimisticId));
      const detail = error?.data?.message
         ? String(error.data.message).trim()
         : error?.message || error?.error
           ? formatCalendarCreateError(error?.message ? error : { message: String(error.error) }, { fallback: "" }).text
           : "";
      return abort(
         detail
            ? `Programarea nu a putut fi creată: ${detail}`
            : "Programarea nu a putut fi creată.",
      );
   }
}
